import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import {
    AssignmentOutlined,
    AttachFileOutlined,
    CalendarMonthOutlined,
    CheckCircleOutlined,
    CloudUploadOutlined,
    FilePresentOutlined,
    SendOutlined,
    SubjectOutlined,
    SchoolOutlined,
    DeleteOutlineOutlined,
    GroupOutlined,
    InfoOutlined,
    AutoAwesomeOutlined,
} from "@mui/icons-material";

import Swal from "sweetalert2";

import api from "../../api/api";

import defaultAvatar from "./maleee.jpg";

import "./AssignmentViewStyles.css";


/* ============================================================
   HELPERS
   ============================================================ */

const resolveAvatar = (value) => {
    if (!value) {
        return defaultAvatar;
    }

    if (typeof value === "string") {
        return value.trim() || defaultAvatar;
    }

    if (typeof value === "object") {
        return (
            value.url ||
            value.image ||
            value.src ||
            value.profile_image ||
            value.avatar ||
            defaultAvatar
        );
    }

    return defaultAvatar;
};


const getEntityLabel = (item) => {
    if (!item) {
        return "";
    }

    if (typeof item === "string") {
        return item;
    }

    return (
        item.name ||
        item.label ||
        item.title ||
        item.subject_name ||
        item.grade_class_name ||
        ""
    );
};


const getList = (data) => {
    if (Array.isArray(data)) {
        return data;
    }

    if (Array.isArray(data?.results)) {
        return data.results;
    }

    if (Array.isArray(data?.data)) {
        return data.data;
    }

    return [];
};


const formatFileSize = (bytes) => {
    if (!bytes) {
        return "0 KB";
    }

    const sizes = [
        "Bytes",
        "KB",
        "MB",
        "GB",
    ];

    const index = Math.floor(
        Math.log(bytes) /
            Math.log(1024)
    );

    return `${(
        bytes /
        Math.pow(
            1024,
            index
        )
    ).toFixed(index === 0 ? 0 : 1)} ${
        sizes[index] || "Bytes"
    }`;
};


const getFileExtension = (filename = "") => {
    const parts =
        filename.split(".");

    return parts.length > 1
        ? parts[
              parts.length - 1
          ].toUpperCase()
        : "FILE";
};


/* ============================================================
   COLORS
   ============================================================ */

const COLORS = {
    purpleDeep: "#24113F",
    purpleDark: "#321653",
    purple: "#4A1D73",
    purpleMedium: "#632A91",
    purpleLight: "#7C3FB1",

    goldDeep: "#A96E00",
    gold: "#D49A16",
    goldLight: "#E7B83F",
    yellow: "#F4C542",
    yellowSoft: "#FFF3C4",

    ivory: "#FFFCF5",
    cream: "#FFF9EA",

    text: "#261C32",
    textSoft: "#554B60",
    muted: "#7B7184",

    border: "#E7DCCB",
    borderPurple: "#DCCCEB",

    white: "#FFFFFF",

    success: "#2F7D4A",
    successSoft: "#EDF8F0",

    danger: "#B44545",
    dangerSoft: "#FFF3F3",
};


/* ============================================================
   COMPONENT
   ============================================================ */

const PostAssignment = () => {
    const [subjects, setSubjects] =
        useState([]);

    const [gradeClasses, setGradeClasses] =
        useState([]);

    const [teacher, setTeacher] =
        useState(null);

    const [form, setForm] =
        useState({
            title: "",
            description: "",
            due_date: "",
            subject: "",
            grade_class: "",
            file: null,
        });

    const [loading, setLoading] =
        useState(false);

    const [fetching, setFetching] =
        useState(true);

    const [error, setError] =
        useState("");

    const [fieldErrors, setFieldErrors] =
        useState({});

    const fileInputRef =
        useRef(null);


    /* ========================================================
       FETCH TEACHER DATA
       ======================================================== */

    useEffect(() => {
        let mounted = true;

        const fetchTeacherData =
            async () => {
                try {
                    setFetching(true);
                    setError("");

                    const response =
                        await api.get(
                            "/api/users/teacher-subjects/"
                        );

                    if (!mounted) {
                        return;
                    }

                    const data =
                        response?.data || {};

                    setSubjects(
                        getList(
                            data.subjects
                        )
                    );

                    setGradeClasses(
                        getList(
                            data.grade_classes
                        )
                    );
                } catch (err) {
                    console.error(
                        "Failed loading teacher information:",
                        err
                    );

                    if (mounted) {
                        setError(
                            err?.response
                                ?.data
                                ?.detail ||
                                "Failed loading teacher information. Please refresh the page and try again."
                        );
                    }
                } finally {
                    if (mounted) {
                        setFetching(false);
                    }
                }
            };

        const fetchProfile =
            async () => {
                try {
                    const response =
                        await api.get(
                            "/api/users/profile/"
                        );

                    if (!mounted) {
                        return;
                    }

                    setTeacher(
                        response?.data ||
                            null
                    );
                } catch (err) {
                    console.error(
                        "Failed loading teacher profile:",
                        err
                    );
                }
            };

        fetchTeacherData();
        fetchProfile();

        return () => {
            mounted = false;
        };
    }, []);


    /* ========================================================
       HANDLE FIELD CHANGE
       ======================================================== */

    const handleChange = (
        event
    ) => {
        const {
            name,
            value,
        } = event.target;

        setForm(
            (previous) => ({
                ...previous,
                [name]: value,
            })
        );

        setFieldErrors(
            (previous) => ({
                ...previous,
                [name]: "",
            })
        );
    };


    /* ========================================================
       FILE CHANGE
       ======================================================== */

    const handleFileChange = (
        event
    ) => {
        const file =
            event.target.files?.[0] ||
            null;

        setForm(
            (previous) => ({
                ...previous,
                file,
            })
        );
    };


    /* ========================================================
       REMOVE FILE
       ======================================================== */

    const removeFile = () => {
        setForm(
            (previous) => ({
                ...previous,
                file: null,
            })
        );

        if (
            fileInputRef.current
        ) {
            fileInputRef.current.value =
                "";
        }
    };


    /* ========================================================
       RESET FORM
       ======================================================== */

    const resetForm = () => {
        setForm({
            title: "",
            description: "",
            due_date: "",
            subject: "",
            grade_class: "",
            file: null,
        });

        setFieldErrors({});

        if (
            fileInputRef.current
        ) {
            fileInputRef.current.value =
                "";
        }
    };


    /* ========================================================
       VALIDATE FORM
       ======================================================== */

    const validateForm = () => {
        const errors = {};

        if (!form.title.trim()) {
            errors.title =
                "Assignment title is required.";
        }

        if (!form.subject) {
            errors.subject =
                "Please select a subject.";
        }

        if (!form.grade_class) {
            errors.grade_class =
                "Please select a grade/class.";
        }

        if (!form.due_date) {
            errors.due_date =
                "Please select a due date.";
        }

        setFieldErrors(errors);

        return (
            Object.keys(errors)
                .length === 0
        );
    };


    /* ========================================================
       SUBMIT
       ======================================================== */

    const handleSubmit = async (
        event
    ) => {
        event.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                icon: "warning",
                title:
                    "Complete the assignment",
                text:
                    "Please fill in all required fields before posting.",
                confirmButtonColor:
                    COLORS.purple,
            });

            return;
        }

        try {
            setLoading(true);

            const formData =
                new FormData();

            formData.append(
                "title",
                form.title.trim()
            );

            formData.append(
                "description",
                form.description.trim()
            );

            formData.append(
                "subject",
                form.subject
            );

            formData.append(
                "grade_class",
                form.grade_class
            );

            formData.append(
                "due_date",
                form.due_date
            );

            if (form.file) {
                formData.append(
                    "attachment",
                    form.file
                );
            }

            await api.post(
                "/api/assignments/",
                formData,
                {
                    headers: {
                        "Content-Type":
                            "multipart/form-data",
                    },
                }
            );

            await Swal.fire({
                icon: "success",
                title:
                    "Assignment posted!",
                text:
                    "Your assignment has been shared with the selected class.",
                confirmButtonColor:
                    COLORS.purple,
            });

            resetForm();
        } catch (err) {
            console.error(
                "Failed to post assignment:",
                err
            );

            let message =
                "The assignment could not be posted. Please try again.";

            const responseData =
                err?.response?.data;

            if (
                responseData?.detail
            ) {
                message =
                    responseData.detail;
            } else if (
                typeof responseData ===
                "object"
            ) {
                const messages =
                    Object.entries(
                        responseData
                    )
                        .map(
                            ([
                                key,
                                value,
                            ]) => {
                                const text =
                                    Array.isArray(
                                        value
                                    )
                                        ? value.join(
                                              ", "
                                          )
                                        : String(
                                              value
                                          );

                                return `${key}: ${text}`;
                            }
                        )
                        .join("\n");

                if (messages) {
                    message = messages;
                }
            } else if (
                err?.message
            ) {
                message =
                    err.message;
            }

            Swal.fire({
                icon: "error",
                title:
                    "Unable to post assignment",
                text: message,
                confirmButtonColor:
                    COLORS.purple,
            });
        } finally {
            setLoading(false);
        }
    };


    /* ========================================================
       SELECTED SUBJECT / CLASS
       ======================================================== */

    const selectedSubject =
        useMemo(() => {
            return subjects.find(
                (item) =>
                    String(
                        item?.id
                    ) ===
                    String(
                        form.subject
                    )
            );
        }, [
            subjects,
            form.subject,
        ]);


    const selectedGradeClass =
        useMemo(() => {
            return gradeClasses.find(
                (item) =>
                    String(
                        item?.id
                    ) ===
                    String(
                        form.grade_class
                    )
            );
        }, [
            gradeClasses,
            form.grade_class,
        ]);


    /* ========================================================
       TEACHER IDENTITY
       ======================================================== */

    const teacherName =
        teacher?.full_name ||
        teacher?.name ||
        teacher?.username ||
        teacher?.user?.full_name ||
        teacher?.user?.username ||
        "Teacher";


    const teacherAvatar =
        resolveAvatar(
            teacher?.profile_image ||
                teacher?.profile_picture ||
                teacher?.avatar ||
                teacher?.image ||
                teacher?.user?.profile_image ||
                teacher?.user?.avatar
        );


    /* ========================================================
       CHECKLIST
       ======================================================== */

    const checklist = [
        {
            label:
                "Assignment title added",
            done:
                Boolean(
                    form.title.trim()
                ),
        },
        {
            label:
                "Subject selected",
            done:
                Boolean(
                    form.subject
                ),
        },
        {
            label:
                "Class selected",
            done:
                Boolean(
                    form.grade_class
                ),
        },
        {
            label:
                "Due date selected",
            done:
                Boolean(
                    form.due_date
                ),
        },
    ];


    /* ========================================================
       INPUT STYLE
       ======================================================== */

    const textFieldSx = {
        "& .MuiOutlinedInput-root": {
            borderRadius: 2,
            background:
                COLORS.white,
            color: COLORS.text,
            "& fieldset": {
                borderColor:
                    COLORS.border,
            },
            "&:hover fieldset": {
                borderColor:
                    COLORS.gold,
            },
            "&.Mui-focused fieldset": {
                borderColor:
                    COLORS.purpleMedium,
                borderWidth: 1.5,
            },
        },

        "& .MuiInputLabel-root": {
            color:
                COLORS.textSoft,
        },

        "& .MuiInputLabel-root.Mui-focused": {
            color:
                COLORS.purple,
            fontWeight: 700,
        },

        "& .MuiFormHelperText-root": {
            color:
                COLORS.muted,
        },

        "& input, & textarea": {
            color:
                COLORS.text,
        },

        "& input::placeholder, & textarea::placeholder": {
            color:
                "#9B919F",
            opacity: 1,
        },
    };


    /* ========================================================
       RENDER
       ======================================================== */

    return (
        <Box
            sx={{
                minHeight:
                    "100vh",

                background:
                    `
                    radial-gradient(
                        circle at 5% 0%,
                        rgba(124,63,177,.18),
                        transparent 28%
                    ),
                    radial-gradient(
                        circle at 96% 5%,
                        rgba(244,197,66,.20),
                        transparent 25%
                    ),
                    radial-gradient(
                        circle at 50% 100%,
                        rgba(212,154,22,.10),
                        transparent 32%
                    ),
                    linear-gradient(
                        135deg,
                        #FFFDF8 0%,
                        #FFF9EC 48%,
                        #F8F3FC 100%
                    )
                    `,

                py: {
                    xs: 2,
                    sm: 3,
                    md: 4,
                },
            }}
        >
            <Container
                maxWidth="lg"
            >

                {/* ====================================================
                    PAGE HEADER
                   ==================================================== */}

                <Paper
                    elevation={0}
                    sx={{
                        position:
                            "relative",
                        overflow:
                            "hidden",
                        mb: 2.5,

                        border:
                            `1px solid rgba(231,220,203,.75)`,

                        borderRadius:
                            {
                                xs: 3,
                                md: 4,
                            },

                        background:
                            `
                            linear-gradient(
                                135deg,
                                #24113F 0%,
                                #321653 35%,
                                #4A1D73 65%,
                                #A96E00 150%
                            )
                            `,

                        boxShadow:
                            `
                            0 18px 50px
                            rgba(36,17,63,.20)
                            `,

                        color:
                            COLORS.white,
                    }}
                >

                    <Box
                        sx={{
                            position:
                                "absolute",
                            width: 320,
                            height: 320,
                            borderRadius:
                                "50%",
                            right: -140,
                            top: -190,
                            background:
                                `
                                radial-gradient(
                                    circle,
                                    rgba(244,197,66,.38),
                                    rgba(244,197,66,.06) 55%,
                                    transparent 72%
                                )
                                `,
                        }}
                    />

                    <Box
                        sx={{
                            position:
                                "absolute",
                            width: 230,
                            height: 230,
                            borderRadius:
                                "50%",
                            left: "40%",
                            bottom: -180,
                            background:
                                "rgba(124,63,177,.30)",
                        }}
                    />

                    <Box
                        sx={{
                            position:
                                "absolute",
                            width: 110,
                            height: 110,
                            borderRadius:
                                "50%",
                            right: "24%",
                            bottom: -65,
                            background:
                                "rgba(244,197,66,.12)",
                            border:
                                "1px solid rgba(244,197,66,.18)",
                        }}
                    />

                    <Box
                        sx={{
                            position:
                                "relative",
                            zIndex: 2,
                            p: {
                                xs: 2.5,
                                sm: 3.5,
                                md: 4,
                            },
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={2}
                            alignItems={{
                                xs: "flex-start",
                                sm: "center",
                            }}
                        >

                            <Avatar
                                src={
                                    teacherAvatar
                                }
                                sx={{
                                    width: {
                                        xs: 58,
                                        sm: 70,
                                    },
                                    height: {
                                        xs: 58,
                                        sm: 70,
                                    },

                                    border:
                                        `3px solid ${COLORS.yellow}`,

                                    boxShadow:
                                        "0 9px 28px rgba(0,0,0,.25)",
                                }}
                            />

                            <Box
                                sx={{
                                    minWidth:
                                        0,
                                    flex: 1,
                                }}
                            >

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                >

                                    <Typography
                                        sx={{
                                            fontSize:
                                                {
                                                    xs: 23,
                                                    sm: 29,
                                                },
                                            fontWeight:
                                                850,
                                            lineHeight:
                                                1.15,
                                            letterSpacing:
                                                "-.6px",
                                            color:
                                                "#FFFFFF",
                                        }}
                                    >
                                        Create Assignment
                                    </Typography>

                                    <Box
                                        sx={{
                                            px: 1.1,
                                            py: 0.4,
                                            borderRadius:
                                                999,
                                            background:
                                                "rgba(244,197,66,.16)",
                                            border:
                                                "1px solid rgba(244,197,66,.45)",
                                            color:
                                                "#FFE9A3",
                                            fontSize:
                                                10,
                                            fontWeight:
                                                850,
                                            letterSpacing:
                                                ".7px",
                                            textTransform:
                                                "uppercase",
                                        }}
                                    >
                                        Teacher Post
                                    </Box>

                                </Stack>

                                <Typography
                                    sx={{
                                        mt: 0.8,
                                        maxWidth:
                                            730,
                                        color:
                                            "rgba(255,255,255,.86)",
                                        fontSize:
                                            {
                                                xs: 12,
                                                sm: 14,
                                            },
                                        lineHeight:
                                            1.7,
                                    }}
                                >
                                    Turn your classroom
                                    task into a clear,
                                    engaging post that
                                    students can easily
                                    discover, understand,
                                    and discuss.
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                    sx={{
                                        mt: 1.6,
                                        gap: 0.8,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            display:
                                                "inline-flex",
                                            alignItems:
                                                "center",
                                            gap: 0.7,
                                            px: 1.2,
                                            py: 0.6,
                                            borderRadius:
                                                999,
                                            background:
                                                "rgba(255,255,255,.10)",
                                            border:
                                                "1px solid rgba(255,255,255,.10)",
                                            color:
                                                "#FFF8DF",
                                            fontSize:
                                                11,
                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        <AssignmentOutlined
                                            sx={{
                                                fontSize:
                                                    14,
                                                color:
                                                    COLORS.yellow,
                                            }}
                                        />
                                        Share a task
                                    </Box>

                                    <Box
                                        sx={{
                                            display:
                                                "inline-flex",
                                            alignItems:
                                                "center",
                                            gap: 0.7,
                                            px: 1.2,
                                            py: 0.6,
                                            borderRadius:
                                                999,
                                            background:
                                                "rgba(255,255,255,.10)",
                                            border:
                                                "1px solid rgba(255,255,255,.10)",
                                            color:
                                                "#FFF8DF",
                                            fontSize:
                                                11,
                                            fontWeight:
                                                700,
                                        }}
                                    >
                                        <GroupOutlined
                                            sx={{
                                                fontSize:
                                                    14,
                                                color:
                                                    COLORS.yellow,
                                            }}
                                        />
                                        Connect with learners
                                    </Box>

                                </Stack>
                            </Box>
                        </Stack>
                    </Box>
                </Paper>


                {/* ====================================================
                    MAIN CONTENT
                   ==================================================== */}

                <Grid
                    container
                    spacing={{
                        xs: 2,
                        md: 2.5,
                    }}
                    alignItems="flex-start"
                >

                    {/* ====================================================
                        FORM
                       ==================================================== */}

                    <Grid
                        item
                        xs={12}
                        md={8}
                    >

                        <Paper
                            elevation={0}
                            sx={{
                                border:
                                    `1px solid ${COLORS.border}`,
                                borderRadius:
                                    {
                                        xs: 3,
                                        md: 3.5,
                                    },
                                background:
                                    "rgba(255,255,255,.96)",
                                boxShadow:
                                    "0 12px 38px rgba(54,31,72,.08)",
                                overflow:
                                    "hidden",
                            }}
                        >

                            {/* FORM HEADER */}

                            <Box
                                sx={{
                                    p: {
                                        xs: 2,
                                        sm: 2.5,
                                        md: 3,
                                    },

                                    background:
                                        `
                                        linear-gradient(
                                            135deg,
                                            #FFFFFF 0%,
                                            #FFFDF7 100%
                                        )
                                        `,
                                }}
                            >

                                <Stack
                                    direction="row"
                                    spacing={1.4}
                                    alignItems="center"
                                >

                                    <Box
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius:
                                                1.7,
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",

                                            background:
                                                `
                                                linear-gradient(
                                                    135deg,
                                                    #321653,
                                                    #632A91
                                                )
                                                `,

                                            color:
                                                COLORS.yellow,

                                            boxShadow:
                                                "0 7px 17px rgba(74,29,115,.18)",
                                        }}
                                    >
                                        <AssignmentOutlined />
                                    </Box>

                                    <Box>
                                        <Typography
                                            sx={{
                                                color:
                                                    COLORS.text,
                                                fontSize:
                                                    18,
                                                fontWeight:
                                                    850,
                                            }}
                                        >
                                            New assignment post
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.3,
                                                color:
                                                    COLORS.textSoft,
                                                fontSize:
                                                    11.5,
                                            }}
                                        >
                                            Complete the
                                            details below
                                            before sharing.
                                        </Typography>
                                    </Box>

                                </Stack>
                            </Box>

                            <Divider
                                sx={{
                                    borderColor:
                                        "#EEE6D9",
                                }}
                            />


                            {/* LOADING */}

                            {fetching && (
                                <Box
                                    sx={{
                                        mx: {
                                            xs: 2,
                                            sm: 2.5,
                                            md: 3,
                                        },
                                        mt: 2,
                                        p: 1.5,

                                        border:
                                            `1px solid ${COLORS.borderPurple}`,

                                        borderRadius:
                                            2,

                                        background:
                                            "#FAF7FF",

                                        color:
                                            COLORS.purple,

                                        fontSize:
                                            12,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >
                                        <CircularProgress
                                            size={
                                                17
                                            }
                                            thickness={
                                                5
                                            }
                                            sx={{
                                                color:
                                                    COLORS.gold,
                                            }}
                                        />

                                        <span>
                                            Loading your
                                            teaching
                                            subjects and
                                            classes...
                                        </span>
                                    </Stack>
                                </Box>
                            )}


                            {/* ERROR */}

                            {error && (
                                <Box
                                    sx={{
                                        mx: {
                                            xs: 2,
                                            sm: 2.5,
                                            md: 3,
                                        },
                                        mt: 2,
                                        p: 1.5,
                                        border:
                                            "1px solid #EACACA",
                                        borderRadius:
                                            2,
                                        background:
                                            COLORS.dangerSoft,
                                        color:
                                            COLORS.danger,
                                        fontSize:
                                            12,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                    >
                                        <InfoOutlined
                                            sx={{
                                                fontSize:
                                                    18,
                                                mt:
                                                    0.1,
                                            }}
                                        />

                                        <span>
                                            {error}
                                        </span>
                                    </Stack>
                                </Box>
                            )}


                            {/* FORM */}

                            <Box
                                component="form"
                                onSubmit={
                                    handleSubmit
                                }
                                sx={{
                                    p: {
                                        xs: 2,
                                        sm: 2.5,
                                        md: 3,
                                    },
                                }}
                            >

                                <Stack
                                    spacing={
                                        2.2
                                    }
                                >

                                    {/* TITLE */}

                                    <TextField
                                        label="Assignment title"
                                        name="title"
                                        value={
                                            form.title
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        fullWidth
                                        error={
                                            Boolean(
                                                fieldErrors.title
                                            )
                                        }
                                        helperText={
                                            fieldErrors.title ||
                                            `${form.title.length}/200 characters`
                                        }
                                        inputProps={{
                                            maxLength: 200,
                                        }}
                                        placeholder="e.g. Mathematics — Fractions Practice"
                                        sx={
                                            textFieldSx
                                        }
                                    />


                                    {/* DESCRIPTION */}

                                    <TextField
                                        label="Assignment description"
                                        name="description"
                                        value={
                                            form.description
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        multiline
                                        minRows={
                                            5
                                        }
                                        maxRows={
                                            10
                                        }
                                        fullWidth
                                        placeholder="Explain what students need to do, what they should submit, and any important instructions..."
                                        helperText={`${form.description.length}/3000 characters`}
                                        inputProps={{
                                            maxLength: 3000,
                                        }}
                                        sx={{
                                            ...textFieldSx,

                                            "& .MuiOutlinedInput-root":
                                                {
                                                    ...textFieldSx[
                                                        "& .MuiOutlinedInput-root"
                                                    ],
                                                    alignItems:
                                                        "flex-start",
                                                },
                                        }}
                                    />


                                    {/* SUBJECT / CLASS */}

                                    <Grid
                                        container
                                        spacing={2}
                                    >

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >

                                            <FormControl
                                                fullWidth
                                                required
                                                error={
                                                    Boolean(
                                                        fieldErrors.subject
                                                    )
                                                }
                                            >

                                                <InputLabel
                                                    sx={{
                                                        color:
                                                            COLORS.textSoft,
                                                        "&.Mui-focused":
                                                            {
                                                                color:
                                                                    COLORS.purple,
                                                            },
                                                    }}
                                                >
                                                    Subject
                                                </InputLabel>

                                                <Select
                                                    name="subject"
                                                    value={
                                                        form.subject
                                                    }
                                                    label="Subject"
                                                    onChange={
                                                        handleChange
                                                    }
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <SubjectOutlined
                                                                sx={{
                                                                    ml:
                                                                        0.5,
                                                                    color:
                                                                        COLORS.purple,
                                                                    fontSize:
                                                                        20,
                                                                }}
                                                            />
                                                        </InputAdornment>
                                                    }
                                                    sx={{
                                                        borderRadius:
                                                            2,
                                                        background:
                                                            COLORS.white,
                                                        color:
                                                            COLORS.text,

                                                        "& .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.border,
                                                            },

                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.gold,
                                                            },

                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.purpleMedium,
                                                            },
                                                    }}
                                                >

                                                    {subjects.length ===
                                                    0 ? (
                                                        <MenuItem
                                                            disabled
                                                        >
                                                            No subjects
                                                            available
                                                        </MenuItem>
                                                    ) : (
                                                        subjects.map(
                                                            (
                                                                subject
                                                            ) => (
                                                                <MenuItem
                                                                    key={
                                                                        subject.id
                                                                    }
                                                                    value={
                                                                        subject.id
                                                                    }
                                                                >
                                                                    {
                                                                        subject.name
                                                                    }
                                                                </MenuItem>
                                                            )
                                                        )
                                                    )}

                                                </Select>

                                                <FormHelperText
                                                    sx={{
                                                        color:
                                                            fieldErrors.subject
                                                                ? undefined
                                                                : COLORS.muted,
                                                    }}
                                                >
                                                    {fieldErrors.subject ||
                                                        "Choose the subject for this assignment."}
                                                </FormHelperText>

                                            </FormControl>

                                        </Grid>


                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >

                                            <FormControl
                                                fullWidth
                                                required
                                                error={
                                                    Boolean(
                                                        fieldErrors.grade_class
                                                    )
                                                }
                                            >

                                                <InputLabel
                                                    sx={{
                                                        color:
                                                            COLORS.textSoft,
                                                        "&.Mui-focused":
                                                            {
                                                                color:
                                                                    COLORS.purple,
                                                            },
                                                    }}
                                                >
                                                    Grade / Class
                                                </InputLabel>

                                                <Select
                                                    name="grade_class"
                                                    value={
                                                        form.grade_class
                                                    }
                                                    label="Grade / Class"
                                                    onChange={
                                                        handleChange
                                                    }
                                                    startAdornment={
                                                        <InputAdornment position="start">
                                                            <SchoolOutlined
                                                                sx={{
                                                                    ml:
                                                                        0.5,
                                                                    color:
                                                                        COLORS.purple,
                                                                    fontSize:
                                                                        20,
                                                                }}
                                                            />
                                                        </InputAdornment>
                                                    }
                                                    sx={{
                                                        borderRadius:
                                                            2,
                                                        background:
                                                            COLORS.white,
                                                        color:
                                                            COLORS.text,

                                                        "& .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.border,
                                                            },

                                                        "&:hover .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.gold,
                                                            },

                                                        "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                            {
                                                                borderColor:
                                                                    COLORS.purpleMedium,
                                                            },
                                                    }}
                                                >

                                                    {gradeClasses.length ===
                                                    0 ? (
                                                        <MenuItem
                                                            disabled
                                                        >
                                                            No classes
                                                            available
                                                        </MenuItem>
                                                    ) : (
                                                        gradeClasses.map(
                                                            (
                                                                item
                                                            ) => (
                                                                <MenuItem
                                                                    key={
                                                                        item.id
                                                                    }
                                                                    value={
                                                                        item.id
                                                                    }
                                                                >
                                                                    {getEntityLabel(
                                                                        item
                                                                    )}
                                                                </MenuItem>
                                                            )
                                                        )
                                                    )}

                                                </Select>

                                                <FormHelperText
                                                    sx={{
                                                        color:
                                                            fieldErrors.grade_class
                                                                ? undefined
                                                                : COLORS.muted,
                                                    }}
                                                >
                                                    {fieldErrors.grade_class ||
                                                        "Choose who should receive this assignment."}
                                                </FormHelperText>

                                            </FormControl>

                                        </Grid>

                                    </Grid>


                                    {/* DEADLINE */}

                                    <TextField
                                        type="date"
                                        label="Due date"
                                        name="due_date"
                                        value={
                                            form.due_date
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        fullWidth
                                        error={
                                            Boolean(
                                                fieldErrors.due_date
                                            )
                                        }
                                        helperText={
                                            fieldErrors.due_date ||
                                            "Students will see this deadline on the assignment."
                                        }
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <CalendarMonthOutlined
                                                            sx={{
                                                                color:
                                                                    COLORS.purple,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={
                                            textFieldSx
                                        }
                                    />


                                    {/* ATTACHMENT */}

                                    <Box
                                        sx={{
                                            border:
                                                `1.5px dashed ${COLORS.goldLight}`,
                                            borderRadius:
                                                2.5,

                                            background:
                                                `
                                                linear-gradient(
                                                    145deg,
                                                    #FFFDF6,
                                                    #FFF8E4
                                                )
                                                `,

                                            p: {
                                                xs: 1.8,
                                                sm: 2.2,
                                            },

                                            transition:
                                                "all .2s ease",

                                            "&:hover": {
                                                borderColor:
                                                    COLORS.purpleMedium,
                                                background:
                                                    "#FBF7FF",
                                            },
                                        }}
                                    >

                                        {!form.file ? (
                                            <Button
                                                component="label"
                                                fullWidth
                                                sx={{
                                                    minHeight:
                                                        105,
                                                    display:
                                                        "flex",
                                                    flexDirection:
                                                        "column",
                                                    gap: 0.8,
                                                    color:
                                                        COLORS.purple,
                                                    textTransform:
                                                        "none",
                                                    borderRadius:
                                                        2,

                                                    "&:hover":
                                                        {
                                                            background:
                                                                "rgba(74,29,115,.045)",
                                                        },
                                                }}
                                            >

                                                <Box
                                                    sx={{
                                                        width: 46,
                                                        height: 46,
                                                        borderRadius:
                                                            "50%",
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",

                                                        background:
                                                            `
                                                            linear-gradient(
                                                                135deg,
                                                                #321653,
                                                                #632A91
                                                            )
                                                            `,

                                                        color:
                                                            COLORS.yellow,

                                                        boxShadow:
                                                            "0 7px 18px rgba(74,29,115,.18)",
                                                    }}
                                                >
                                                    <CloudUploadOutlined
                                                        sx={{
                                                            fontSize:
                                                                25,
                                                        }}
                                                    />
                                                </Box>

                                                <Typography
                                                    sx={{
                                                        fontSize:
                                                            13,
                                                        fontWeight:
                                                            850,
                                                        color:
                                                            COLORS.text,
                                                    }}
                                                >
                                                    Add an attachment
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color:
                                                            COLORS.muted,
                                                        fontSize:
                                                            11,
                                                        textAlign:
                                                            "center",
                                                    }}
                                                >
                                                    Upload a
                                                    document,
                                                    worksheet,
                                                    image, or
                                                    other
                                                    supporting
                                                    file.
                                                </Typography>

                                                <input
                                                    hidden
                                                    type="file"
                                                    ref={
                                                        fileInputRef
                                                    }
                                                    onChange={
                                                        handleFileChange
                                                    }
                                                />

                                            </Button>
                                        ) : (

                                            <Stack
                                                direction="row"
                                                spacing={
                                                    1.3
                                                }
                                                alignItems="center"
                                            >

                                                <Box
                                                    sx={{
                                                        width: 46,
                                                        height: 46,
                                                        borderRadius:
                                                            1.5,
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        flex:
                                                            "0 0 auto",

                                                        background:
                                                            "#F5EBD0",

                                                        color:
                                                            COLORS.goldDeep,
                                                    }}
                                                >
                                                    <FilePresentOutlined />
                                                </Box>

                                                <Box
                                                    sx={{
                                                        minWidth:
                                                            0,
                                                        flex: 1,
                                                    }}
                                                >

                                                    <Typography
                                                        sx={{
                                                            color:
                                                                COLORS.text,
                                                            fontSize:
                                                                12,
                                                            fontWeight:
                                                                800,
                                                            overflow:
                                                                "hidden",
                                                            textOverflow:
                                                                "ellipsis",
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {
                                                            form.file
                                                                .name
                                                        }
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            mt:
                                                                0.2,
                                                            color:
                                                                COLORS.muted,
                                                            fontSize:
                                                                10,
                                                        }}
                                                    >
                                                        {
                                                            getFileExtension(
                                                                form.file
                                                                    .name
                                                            )
                                                        }{" "}
                                                        •{" "}
                                                        {
                                                            formatFileSize(
                                                                form.file
                                                                    .size
                                                            )
                                                        }
                                                    </Typography>

                                                </Box>

                                                <Button
                                                    type="button"
                                                    onClick={
                                                        removeFile
                                                    }
                                                    disabled={
                                                        loading
                                                    }
                                                    sx={{
                                                        minWidth:
                                                            34,
                                                        width: 34,
                                                        height: 34,
                                                        borderRadius:
                                                            1.2,
                                                        color:
                                                            COLORS.danger,

                                                        "&:hover":
                                                            {
                                                                background:
                                                                    COLORS.dangerSoft,
                                                            },
                                                    }}
                                                >
                                                    <DeleteOutlineOutlined
                                                        fontSize="small"
                                                    />
                                                </Button>

                                            </Stack>

                                        )}

                                    </Box>


                                    {/* POST BUTTON */}

                                    <Button
                                        type="submit"
                                        fullWidth
                                        disabled={
                                            loading ||
                                            fetching
                                        }
                                        startIcon={
                                            loading ? (
                                                <CircularProgress
                                                    size={
                                                        19
                                                    }
                                                    sx={{
                                                        color:
                                                            COLORS.white,
                                                    }}
                                                />
                                            ) : (
                                                <SendOutlined />
                                            )
                                        }
                                        sx={{
                                            minHeight:
                                                52,
                                            borderRadius:
                                                2,

                                            color:
                                                "#FFFFFF",

                                            background:
                                                `
                                                linear-gradient(
                                                    135deg,
                                                    #24113F 0%,
                                                    #4A1D73 48%,
                                                    #A96E00 100%
                                                )
                                                `,

                                            fontSize:
                                                13,
                                            fontWeight:
                                                850,
                                            textTransform:
                                                "none",

                                            boxShadow:
                                                `
                                                0 10px 26px
                                                rgba(74,29,115,.20)
                                                `,

                                            "&:hover":
                                                {
                                                    background:
                                                        `
                                                        linear-gradient(
                                                            135deg,
                                                            #1D0C34 0%,
                                                            #3D175F 48%,
                                                            #8D5B00 100%
                                                        )
                                                        `,

                                                    boxShadow:
                                                        `
                                                        0 13px 31px
                                                        rgba(74,29,115,.27)
                                                        `,
                                                },

                                            "&.Mui-disabled":
                                                {
                                                    color:
                                                        "rgba(255,255,255,.72)",
                                                    background:
                                                        "#A99DB2",
                                                },
                                        }}
                                    >
                                        {loading
                                            ? "Posting assignment..."
                                            : "Post assignment"}
                                    </Button>

                                </Stack>

                            </Box>

                        </Paper>

                    </Grid>


                    {/* ====================================================
                        SIDEBAR
                       ==================================================== */}

                    <Grid
                        item
                        xs={12}
                        md={4}
                    >

                        <Stack
                            spacing={2}
                            sx={{
                                position:
                                    {
                                        xs:
                                            "static",
                                        md:
                                            "sticky",
                                    },
                                top: {
                                    md: 20,
                                },
                            }}
                        >

                            {/* =================================================
                                LIVE PREVIEW
                               ================================================= */}

                            <Paper
                                elevation={0}
                                sx={{
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    borderRadius:
                                        3,
                                    background:
                                        COLORS.white,
                                    boxShadow:
                                        "0 10px 30px rgba(54,31,72,.07)",
                                    overflow:
                                        "hidden",
                                }}
                            >

                                <Box
                                    sx={{
                                        px: 2.2,
                                        py: 1.8,

                                        background:
                                            `
                                            linear-gradient(
                                                135deg,
                                                #FFFDF8,
                                                #FAF5FF
                                            )
                                            `,

                                        borderBottom:
                                            "1px solid #EEE6D9",
                                    }}
                                >

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >

                                        <Box
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius:
                                                    1.2,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",

                                                background:
                                                    `
                                                    linear-gradient(
                                                        135deg,
                                                        #321653,
                                                        #632A91
                                                    )
                                                    `,

                                                color:
                                                    COLORS.yellow,
                                            }}
                                        >
                                            <AutoAwesomeOutlined
                                                fontSize="small"
                                            />
                                        </Box>

                                        <Box>
                                            <Typography
                                                sx={{
                                                    color:
                                                        COLORS.text,
                                                    fontSize:
                                                        13,
                                                    fontWeight:
                                                        850,
                                                }}
                                            >
                                                Post preview
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color:
                                                        COLORS.muted,
                                                    fontSize:
                                                        10,
                                                }}
                                            >
                                                How students
                                                will see it
                                            </Typography>
                                        </Box>

                                    </Stack>

                                </Box>


                                <Box
                                    sx={{
                                        p: 2.2,
                                    }}
                                >

                                    {/* AUTHOR */}

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="center"
                                    >

                                        <Avatar
                                            src={
                                                teacherAvatar
                                            }
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                border:
                                                    `2px solid ${COLORS.goldLight}`,
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                minWidth:
                                                    0,
                                            }}
                                        >

                                            <Typography
                                                sx={{
                                                    color:
                                                        COLORS.text,
                                                    fontSize:
                                                        12,
                                                    fontWeight:
                                                        850,
                                                }}
                                            >
                                                {
                                                    teacherName
                                                }
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    color:
                                                        COLORS.muted,
                                                    fontSize:
                                                        10,
                                                }}
                                            >
                                                Teacher •
                                                Assignment
                                            </Typography>

                                        </Box>

                                    </Stack>


                                    {/* TITLE */}

                                    <Typography
                                        sx={{
                                            mt: 2,

                                            color:
                                                form.title
                                                    ? COLORS.text
                                                    : "#9D96A2",

                                            fontSize:
                                                16,
                                            fontWeight:
                                                850,
                                            lineHeight:
                                                1.4,

                                            wordBreak:
                                                "break-word",
                                        }}
                                    >
                                        {form.title ||
                                            "Your assignment title will appear here"}
                                    </Typography>


                                    {/* DESCRIPTION */}

                                    <Typography
                                        sx={{
                                            mt: 0.8,

                                            color:
                                                form.description
                                                    ? COLORS.textSoft
                                                    : "#9D96A2",

                                            fontSize:
                                                11.5,
                                            lineHeight:
                                                1.7,

                                            display:
                                                "-webkit-box",

                                            WebkitLineClamp:
                                                5,

                                            WebkitBoxOrient:
                                                "vertical",

                                            overflow:
                                                "hidden",

                                            wordBreak:
                                                "break-word",
                                        }}
                                    >
                                        {form.description ||
                                            "Your assignment description will appear here so you can review the post before sharing it."}
                                    </Typography>


                                    {/* TAGS */}

                                    <Stack
                                        direction="row"
                                        spacing={
                                            0.7
                                        }
                                        flexWrap="wrap"
                                        sx={{
                                            mt: 1.5,
                                            gap: 0.7,
                                        }}
                                    >

                                        {selectedSubject && (
                                            <Box
                                                sx={{
                                                    px: 1,
                                                    py: 0.48,
                                                    borderRadius:
                                                        999,

                                                    background:
                                                        "#F2EAFE",

                                                    color:
                                                        COLORS.purple,

                                                    border:
                                                        "1px solid #E3D2F4",

                                                    fontSize:
                                                        9.5,
                                                    fontWeight:
                                                        800,
                                                }}
                                            >
                                                {
                                                    getEntityLabel(
                                                        selectedSubject
                                                    )
                                                }
                                            </Box>
                                        )}

                                        {selectedGradeClass && (
                                            <Box
                                                sx={{
                                                    px: 1,
                                                    py: 0.48,
                                                    borderRadius:
                                                        999,

                                                    background:
                                                        "#FFF3CC",

                                                    color:
                                                        COLORS.goldDeep,

                                                    border:
                                                        "1px solid #F0D889",

                                                    fontSize:
                                                        9.5,
                                                    fontWeight:
                                                        800,
                                                }}
                                            >
                                                {
                                                    getEntityLabel(
                                                        selectedGradeClass
                                                    )
                                                }
                                            </Box>
                                        )}

                                    </Stack>


                                    {/* DUE DATE */}

                                    {form.due_date && (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1,
                                                mt: 1.5,
                                                p: 1.1,

                                                borderRadius:
                                                    1.5,

                                                background:
                                                    "#FFF9E9",

                                                border:
                                                    "1px solid #F0E0B2",
                                            }}
                                        >

                                            <Box
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    borderRadius:
                                                        1,
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    justifyContent:
                                                        "center",

                                                    background:
                                                        "#F6E9BD",

                                                    color:
                                                        COLORS.goldDeep,
                                                }}
                                            >
                                                <CalendarMonthOutlined
                                                    sx={{
                                                        fontSize:
                                                            17,
                                                    }}
                                                />
                                            </Box>

                                            <Box>

                                                <Typography
                                                    sx={{
                                                        color:
                                                            COLORS.muted,
                                                        fontSize:
                                                            8.5,
                                                        fontWeight:
                                                            850,
                                                        textTransform:
                                                            "uppercase",
                                                        letterSpacing:
                                                            ".5px",
                                                    }}
                                                >
                                                    Due date
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        color:
                                                            COLORS.text,
                                                        fontSize:
                                                            10.5,
                                                        fontWeight:
                                                            750,
                                                    }}
                                                >
                                                    {
                                                        form.due_date
                                                    }
                                                </Typography>

                                            </Box>

                                        </Box>
                                    )}


                                    {/* FILE */}

                                    {form.file && (
                                        <Box
                                            sx={{
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                gap: 1,
                                                mt: 1,

                                                p: 1,

                                                borderRadius:
                                                    1.5,

                                                background:
                                                    "#FAF7FF",

                                                border:
                                                    "1px solid #E8DDF2",
                                            }}
                                        >

                                            <AttachFileOutlined
                                                sx={{
                                                    fontSize:
                                                        16,
                                                    color:
                                                        COLORS.purple,
                                                }}
                                            />

                                            <Typography
                                                sx={{
                                                    color:
                                                        COLORS.textSoft,
                                                    fontSize:
                                                        10,
                                                    overflow:
                                                        "hidden",
                                                    textOverflow:
                                                        "ellipsis",
                                                    whiteSpace:
                                                        "nowrap",
                                                }}
                                            >
                                                {
                                                    form.file
                                                        .name
                                                }
                                            </Typography>

                                        </Box>
                                    )}

                                </Box>

                            </Paper>


                            {/* =================================================
                                COMMUNITY NOTE
                               ================================================= */}

                            <Paper
                                elevation={0}
                                sx={{
                                    border:
                                        `1px solid ${COLORS.borderPurple}`,
                                    borderRadius:
                                        3,
                                    p: 2.2,

                                    background:
                                        `
                                        linear-gradient(
                                            145deg,
                                            #FFFFFF 0%,
                                            #FAF6FF 100%
                                        )
                                        `,

                                    boxShadow:
                                        "0 9px 27px rgba(54,31,72,.06)",
                                }}
                            >

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="flex-start"
                                >

                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius:
                                                1.2,
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",
                                            flex:
                                                "0 0 auto",

                                            background:
                                                "#F2EAFE",

                                            color:
                                                COLORS.purple,
                                        }}
                                    >
                                        <InfoOutlined
                                            fontSize="small"
                                        />
                                    </Box>

                                    <Box>

                                        <Typography
                                            sx={{
                                                color:
                                                    COLORS.text,
                                                fontSize:
                                                    12,
                                                fontWeight:
                                                    850,
                                            }}
                                        >
                                            Make your post useful
                                        </Typography>

                                        <Typography
                                            sx={{
                                                mt: 0.6,
                                                color:
                                                    COLORS.textSoft,
                                                fontSize:
                                                    10.5,
                                                lineHeight:
                                                    1.7,
                                            }}
                                        >
                                            Give students
                                            enough context
                                            to understand
                                            the task without
                                            needing to guess
                                            what you expect.
                                        </Typography>

                                    </Box>

                                </Stack>

                            </Paper>


                            {/* =================================================
                                CHECKLIST
                               ================================================= */}

                            <Paper
                                elevation={0}
                                sx={{
                                    border:
                                        `1px solid ${COLORS.border}`,
                                    borderRadius:
                                        3,
                                    p: 2.2,
                                    background:
                                        COLORS.white,
                                    boxShadow:
                                        "0 9px 27px rgba(54,31,72,.05)",
                                }}
                            >

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{
                                        mb: 1.5,
                                    }}
                                >

                                    <Box
                                        sx={{
                                            width: 30,
                                            height: 30,
                                            borderRadius:
                                                1,
                                            display:
                                                "flex",
                                            alignItems:
                                                "center",
                                            justifyContent:
                                                "center",

                                            background:
                                                "#FFF3CC",

                                            color:
                                                COLORS.goldDeep,
                                        }}
                                    >
                                        <CheckCircleOutlined
                                            sx={{
                                                fontSize:
                                                    17,
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        sx={{
                                            color:
                                                COLORS.text,
                                            fontSize:
                                                12,
                                            fontWeight:
                                                850,
                                        }}
                                    >
                                        Before you post
                                    </Typography>

                                </Stack>


                                <Stack
                                    spacing={
                                        1.1
                                    }
                                >

                                    {checklist.map(
                                        (
                                            item
                                        ) => (
                                            <Stack
                                                key={
                                                    item.label
                                                }
                                                direction="row"
                                                spacing={
                                                    0.8
                                                }
                                                alignItems="center"
                                            >

                                                <CheckCircleOutlined
                                                    sx={{
                                                        fontSize:
                                                            17,

                                                        color:
                                                            item.done
                                                                ? COLORS.success
                                                                : "#C9C1CC",
                                                    }}
                                                />

                                                <Typography
                                                    sx={{
                                                        color:
                                                            item.done
                                                                ? "#466050"
                                                                : COLORS.muted,

                                                        fontSize:
                                                            10.5,

                                                        fontWeight:
                                                            item.done
                                                                ? 650
                                                                : 500,
                                                    }}
                                                >
                                                    {
                                                        item.label
                                                    }
                                                </Typography>

                                            </Stack>
                                        )
                                    )}

                                </Stack>

                            </Paper>

                        </Stack>

                    </Grid>

                </Grid>

            </Container>
        </Box>
    );
};

export default PostAssignment;


// import React, {
//     useEffect,
//     useMemo,
//     useRef,
//     useState,
// } from "react";

// import {
//     Avatar,
//     Box,
//     Button,
//     CircularProgress,
//     Container,
//     Divider,
//     FormControl,
//     FormHelperText,
//     Grid,
//     InputAdornment,
//     InputLabel,
//     MenuItem,
//     Paper,
//     Select,
//     Stack,
//     TextField,
//     Typography,
// } from "@mui/material";

// import {
//     AssignmentOutlined,
//     AttachFileOutlined,
//     CalendarMonthOutlined,
//     CheckCircleOutlined,
//     CloudUploadOutlined,
//     DescriptionOutlined,
//     FilePresentOutlined,
//     SchoolOutlined,
//     SendOutlined,
//     SubjectOutlined,
//     DeleteOutlineOutlined,
//     GroupOutlined,
//     InfoOutlined,
//     AutoAwesomeOutlined,
// } from "@mui/icons-material";

// import Swal from "sweetalert2";

// import api from "../../api/api";

// import defaultAvatar from "./maleee.jpg";

// import "./AssignmentViewStyles.css";


// /* ============================================================
//    HELPERS
//    ============================================================ */

// const resolveAvatar = (value) => {
//     if (!value) {
//         return defaultAvatar;
//     }

//     if (typeof value === "string") {
//         return value.trim() || defaultAvatar;
//     }

//     if (typeof value === "object") {
//         return (
//             value.url ||
//             value.image ||
//             value.src ||
//             value.profile_image ||
//             value.avatar ||
//             defaultAvatar
//         );
//     }

//     return defaultAvatar;
// };


// const getEntityLabel = (item) => {
//     if (!item) {
//         return "";
//     }

//     if (typeof item === "string") {
//         return item;
//     }

//     return (
//         item.name ||
//         item.label ||
//         item.title ||
//         item.subject_name ||
//         item.grade_class_name ||
//         ""
//     );
// };


// const getList = (data) => {
//     if (Array.isArray(data)) {
//         return data;
//     }

//     if (Array.isArray(data?.results)) {
//         return data.results;
//     }

//     if (Array.isArray(data?.data)) {
//         return data.data;
//     }

//     return [];
// };


// const formatFileSize = (bytes) => {
//     if (!bytes) {
//         return "0 KB";
//     }

//     const sizes = [
//         "Bytes",
//         "KB",
//         "MB",
//         "GB",
//     ];

//     const index = Math.floor(
//         Math.log(bytes) /
//             Math.log(1024)
//     );

//     return `${(
//         bytes /
//         Math.pow(
//             1024,
//             index
//         )
//     ).toFixed(index === 0 ? 0 : 1)} ${
//         sizes[index] || "Bytes"
//     }`;
// };


// const getFileExtension = (filename = "") => {
//     const parts =
//         filename.split(".");

//     return parts.length > 1
//         ? parts[
//               parts.length - 1
//           ].toUpperCase()
//         : "FILE";
// };


// /* ============================================================
//    COMPONENT
//    ============================================================ */

// const PostAssignment = () => {
//     const [subjects, setSubjects] =
//         useState([]);

//     const [gradeClasses, setGradeClasses] =
//         useState([]);

//     const [teacher, setTeacher] =
//         useState(null);

//     const [form, setForm] =
//         useState({
//             title: "",
//             description: "",
//             due_date: "",
//             subject: "",
//             grade_class: "",
//             file: null,
//         });

//     const [loading, setLoading] =
//         useState(false);

//     const [fetching, setFetching] =
//         useState(true);

//     const [error, setError] =
//         useState("");

//     const [fieldErrors, setFieldErrors] =
//         useState({});

//     const fileInputRef =
//         useRef(null);


//     /* ========================================================
//        FETCH TEACHER DATA
//        ======================================================== */

//     useEffect(() => {
//         let mounted = true;

//         const fetchTeacherData =
//             async () => {
//                 try {
//                     setFetching(true);
//                     setError("");

//                     const response =
//                         await api.get(
//                             "/api/users/teacher-subjects/"
//                         );

//                     if (!mounted) {
//                         return;
//                     }

//                     const data =
//                         response?.data || {};

//                     setSubjects(
//                         getList(
//                             data.subjects
//                         )
//                     );

//                     setGradeClasses(
//                         getList(
//                             data.grade_classes
//                         )
//                     );
//                 } catch (err) {
//                     console.error(
//                         "Failed loading teacher information:",
//                         err
//                     );

//                     if (mounted) {
//                         setError(
//                             err?.response
//                                 ?.data
//                                 ?.detail ||
//                                 "Failed loading teacher information. Please refresh the page and try again."
//                         );
//                     }
//                 } finally {
//                     if (mounted) {
//                         setFetching(false);
//                     }
//                 }
//             };

//         const fetchProfile =
//             async () => {
//                 try {
//                     const response =
//                         await api.get(
//                             "/api/users/profile/"
//                         );

//                     if (!mounted) {
//                         return;
//                     }

//                     setTeacher(
//                         response?.data ||
//                             null
//                     );
//                 } catch (err) {
//                     console.error(
//                         "Failed loading teacher profile:",
//                         err
//                     );
//                 }
//             };

//         fetchTeacherData();
//         fetchProfile();

//         return () => {
//             mounted = false;
//         };
//     }, []);


//     /* ========================================================
//        HANDLE FIELD CHANGE
//        ======================================================== */

//     const handleChange = (
//         event
//     ) => {
//         const {
//             name,
//             value,
//         } = event.target;

//         setForm(
//             (previous) => ({
//                 ...previous,
//                 [name]: value,
//             })
//         );

//         setFieldErrors(
//             (previous) => ({
//                 ...previous,
//                 [name]: "",
//             })
//         );
//     };


//     /* ========================================================
//        FILE CHANGE
//        ======================================================== */

//     const handleFileChange = (
//         event
//     ) => {
//         const file =
//             event.target.files?.[0] ||
//             null;

//         setForm(
//             (previous) => ({
//                 ...previous,
//                 file,
//             })
//         );
//     };


//     /* ========================================================
//        REMOVE FILE
//        ======================================================== */

//     const removeFile = () => {
//         setForm(
//             (previous) => ({
//                 ...previous,
//                 file: null,
//             })
//         );

//         if (
//             fileInputRef.current
//         ) {
//             fileInputRef.current.value =
//                 "";
//         }
//     };


//     /* ========================================================
//        RESET FORM
//        ======================================================== */

//     const resetForm = () => {
//         setForm({
//             title: "",
//             description: "",
//             due_date: "",
//             subject: "",
//             grade_class: "",
//             file: null,
//         });

//         setFieldErrors({});

//         if (
//             fileInputRef.current
//         ) {
//             fileInputRef.current.value =
//                 "";
//         }
//     };


//     /* ========================================================
//        VALIDATE FORM
//        ======================================================== */

//     const validateForm = () => {
//         const errors = {};

//         if (!form.title.trim()) {
//             errors.title =
//                 "Assignment title is required.";
//         }

//         if (!form.subject) {
//             errors.subject =
//                 "Please select a subject.";
//         }

//         if (!form.grade_class) {
//             errors.grade_class =
//                 "Please select a grade/class.";
//         }

//         if (!form.due_date) {
//             errors.due_date =
//                 "Please select a due date.";
//         }

//         setFieldErrors(errors);

//         return (
//             Object.keys(errors)
//                 .length === 0
//         );
//     };


//     /* ========================================================
//        SUBMIT
//        ======================================================== */

//     const handleSubmit = async (
//         event
//     ) => {
//         event.preventDefault();

//         if (!validateForm()) {
//             Swal.fire({
//                 icon: "warning",
//                 title: "Complete the assignment",
//                 text:
//                     "Please fill in all required fields before posting.",
//                 confirmButtonColor:
//                     "#0B6B5E",
//             });

//             return;
//         }

//         try {
//             setLoading(true);

//             const formData =
//                 new FormData();

//             formData.append(
//                 "title",
//                 form.title.trim()
//             );

//             formData.append(
//                 "description",
//                 form.description.trim()
//             );

//             formData.append(
//                 "subject",
//                 form.subject
//             );

//             formData.append(
//                 "grade_class",
//                 form.grade_class
//             );

//             formData.append(
//                 "due_date",
//                 form.due_date
//             );

//             if (form.file) {
//                 formData.append(
//                     "attachment",
//                     form.file
//                 );
//             }

//             await api.post(
//                 "/api/assignments/",
//                 formData,
//                 {
//                     headers: {
//                         "Content-Type":
//                             "multipart/form-data",
//                     },
//                 }
//             );

//             await Swal.fire({
//                 icon: "success",
//                 title:
//                     "Assignment posted!",
//                 text:
//                     "Your assignment has been shared with the selected class.",
//                 confirmButtonColor:
//                     "#0B6B5E",
//             });

//             resetForm();
//         } catch (err) {
//             console.error(
//                 "Failed to post assignment:",
//                 err
//             );

//             let message =
//                 "The assignment could not be posted. Please try again.";

//             const responseData =
//                 err?.response?.data;

//             if (
//                 responseData?.detail
//             ) {
//                 message =
//                     responseData.detail;
//             } else if (
//                 typeof responseData ===
//                 "object"
//             ) {
//                 const messages =
//                     Object.entries(
//                         responseData
//                     )
//                         .map(
//                             ([
//                                 key,
//                                 value,
//                             ]) => {
//                                 const text =
//                                     Array.isArray(
//                                         value
//                                     )
//                                         ? value.join(
//                                               ", "
//                                           )
//                                         : String(
//                                               value
//                                           );

//                                 return `${key}: ${text}`;
//                             }
//                         )
//                         .join("\n");

//                 if (messages) {
//                     message = messages;
//                 }
//             } else if (
//                 err?.message
//             ) {
//                 message =
//                     err.message;
//             }

//             Swal.fire({
//                 icon: "error",
//                 title:
//                     "Unable to post assignment",
//                 text: message,
//                 confirmButtonColor:
//                     "#0B6B5E",
//             });
//         } finally {
//             setLoading(false);
//         }
//     };


//     /* ========================================================
//        SELECTED SUBJECT / CLASS
//        ======================================================== */

//     const selectedSubject =
//         useMemo(() => {
//             return subjects.find(
//                 (item) =>
//                     String(
//                         item?.id
//                     ) ===
//                     String(
//                         form.subject
//                     )
//             );
//         }, [
//             subjects,
//             form.subject,
//         ]);


//     const selectedGradeClass =
//         useMemo(() => {
//             return gradeClasses.find(
//                 (item) =>
//                     String(
//                         item?.id
//                     ) ===
//                     String(
//                         form.grade_class
//                     )
//             );
//         }, [
//             gradeClasses,
//             form.grade_class,
//         ]);


//     /* ========================================================
//        TEACHER IDENTITY
//        ======================================================== */

//     const teacherName =
//         teacher?.full_name ||
//         teacher?.name ||
//         teacher?.username ||
//         teacher?.user?.full_name ||
//         teacher?.user?.username ||
//         "Teacher";


//     const teacherAvatar =
//         resolveAvatar(
//             teacher?.profile_image ||
//                 teacher?.profile_picture ||
//                 teacher?.avatar ||
//                 teacher?.image ||
//                 teacher?.user?.profile_image ||
//                 teacher?.user?.avatar
//         );


//     /* ========================================================
//        RENDER
//        ======================================================== */

//     return (
//         <Box
//             sx={{
//                 minHeight:
//                     "100vh",
//                 background:
//                     `
//                     radial-gradient(
//                         circle at 0% 0%,
//                         rgba(20,184,166,.08),
//                         transparent 30%
//                     ),
//                     radial-gradient(
//                         circle at 100% 10%,
//                         rgba(56,169,214,.07),
//                         transparent 28%
//                     ),
//                     #f5f9f8
//                     `,
//                 py: {
//                     xs: 2,
//                     sm: 3,
//                     md: 4,
//                 },
//             }}
//         >
//             <Container
//                 maxWidth="lg"
//             >

//                 {/* ====================================================
//                     PAGE HEADER
//                    ==================================================== */}

//                 <Paper
//                     elevation={0}
//                     sx={{
//                         position:
//                             "relative",
//                         overflow:
//                             "hidden",
//                         mb: 2.5,
//                         border:
//                             "1px solid #DDE9E6",
//                         borderRadius:
//                             {
//                                 xs: 3,
//                                 md: 4,
//                             },
//                         background:
//                             "linear-gradient(135deg,#075247 0%,#0B6B5E 45%,#14B8A6 100%)",
//                         boxShadow:
//                             "0 15px 45px rgba(7,82,71,.14)",
//                         color: "#fff",
//                     }}
//                 >
//                     <Box
//                         sx={{
//                             position:
//                                 "absolute",
//                             width: 260,
//                             height: 260,
//                             borderRadius:
//                                 "50%",
//                             right: -100,
//                             top: -150,
//                             background:
//                                 "rgba(255,255,255,.08)",
//                         }}
//                     />

//                     <Box
//                         sx={{
//                             position:
//                                 "absolute",
//                             width: 180,
//                             height: 180,
//                             borderRadius:
//                                 "50%",
//                             left: "45%",
//                             bottom: -140,
//                             background:
//                                 "rgba(255,255,255,.06)",
//                         }}
//                     />

//                     <Box
//                         sx={{
//                             position:
//                                 "relative",
//                             zIndex: 2,
//                             p: {
//                                 xs: 2.5,
//                                 sm: 3.5,
//                                 md: 4,
//                             },
//                         }}
//                     >
//                         <Stack
//                             direction={{
//                                 xs: "column",
//                                 sm: "row",
//                             }}
//                             spacing={2}
//                             alignItems={{
//                                 xs: "flex-start",
//                                 sm: "center",
//                             }}
//                         >
//                             <Avatar
//                                 src={
//                                     teacherAvatar
//                                 }
//                                 sx={{
//                                     width: {
//                                         xs: 58,
//                                         sm: 68,
//                                     },
//                                     height: {
//                                         xs: 58,
//                                         sm: 68,
//                                     },
//                                     border:
//                                         "3px solid rgba(255,255,255,.9)",
//                                     boxShadow:
//                                         "0 8px 22px rgba(0,0,0,.18)",
//                                 }}
//                             />

//                             <Box
//                                 sx={{
//                                     minWidth: 0,
//                                     flex: 1,
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1}
//                                     alignItems="center"
//                                     flexWrap="wrap"
//                                 >
//                                     <Typography
//                                         sx={{
//                                             fontSize:
//                                                 {
//                                                     xs: 23,
//                                                     sm: 28,
//                                                 },
//                                             fontWeight:
//                                                 800,
//                                             lineHeight:
//                                                 1.15,
//                                             letterSpacing:
//                                                 "-.5px",
//                                         }}
//                                     >
//                                         Create Assignment
//                                     </Typography>

//                                     <Box
//                                         sx={{
//                                             px: 1,
//                                             py: 0.35,
//                                             borderRadius:
//                                                 999,
//                                             background:
//                                                 "rgba(255,255,255,.14)",
//                                             border:
//                                                 "1px solid rgba(255,255,255,.2)",
//                                             fontSize:
//                                                 10,
//                                             fontWeight:
//                                                 800,
//                                             letterSpacing:
//                                                 ".6px",
//                                             textTransform:
//                                                 "uppercase",
//                                         }}
//                                     >
//                                         Teacher Post
//                                     </Box>
//                                 </Stack>

//                                 <Typography
//                                     sx={{
//                                         mt: 0.7,
//                                         maxWidth:
//                                             720,
//                                         color:
//                                             "rgba(255,255,255,.82)",
//                                         fontSize:
//                                             {
//                                                 xs: 12,
//                                                 sm: 14,
//                                             },
//                                         lineHeight:
//                                             1.65,
//                                     }}
//                                 >
//                                     Turn your classroom
//                                     task into a clear,
//                                     engaging post that
//                                     students can easily
//                                     discover and discuss.
//                                 </Typography>

//                                 <Stack
//                                     direction="row"
//                                     spacing={1}
//                                     flexWrap="wrap"
//                                     sx={{
//                                         mt: 1.5,
//                                     }}
//                                 >
//                                     <Box
//                                         sx={{
//                                             display:
//                                                 "inline-flex",
//                                             alignItems:
//                                                 "center",
//                                             gap: 0.7,
//                                             px: 1.2,
//                                             py: 0.55,
//                                             borderRadius:
//                                                 999,
//                                             background:
//                                                 "rgba(255,255,255,.11)",
//                                             fontSize:
//                                                 11,
//                                             fontWeight:
//                                                 650,
//                                         }}
//                                     >
//                                         <AssignmentOutlined
//                                             sx={{
//                                                 fontSize:
//                                                     14,
//                                             }}
//                                         />
//                                         Share a task
//                                     </Box>

//                                     <Box
//                                         sx={{
//                                             display:
//                                                 "inline-flex",
//                                             alignItems:
//                                                 "center",
//                                             gap: 0.7,
//                                             px: 1.2,
//                                             py: 0.55,
//                                             borderRadius:
//                                                 999,
//                                             background:
//                                                 "rgba(255,255,255,.11)",
//                                             fontSize:
//                                                 11,
//                                             fontWeight:
//                                                 650,
//                                         }}
//                                     >
//                                         <GroupOutlined
//                                             sx={{
//                                                 fontSize:
//                                                     14,
//                                             }}
//                                         />
//                                         Connect with learners
//                                     </Box>
//                                 </Stack>
//                             </Box>
//                         </Stack>
//                     </Box>
//                 </Paper>


//                 {/* ====================================================
//                     MAIN CONTENT
//                    ==================================================== */}

//                 <Grid
//                     container
//                     spacing={{
//                         xs: 2,
//                         md: 2.5,
//                     }}
//                     alignItems="flex-start"
//                 >

//                     {/* ====================================================
//                         FORM
//                        ==================================================== */}

//                     <Grid
//                         item
//                         xs={12}
//                         md={8}
//                     >
//                         <Paper
//                             elevation={0}
//                             sx={{
//                                 border:
//                                     "1px solid #DDE9E6",
//                                 borderRadius:
//                                     {
//                                         xs: 3,
//                                         md: 3.5,
//                                     },
//                                 background:
//                                     "#ffffff",
//                                 boxShadow:
//                                     "0 10px 35px rgba(7,82,71,.07)",
//                                 overflow:
//                                     "hidden",
//                             }}
//                         >

//                             {/* FORM HEADER */}

//                             <Box
//                                 sx={{
//                                     p: {
//                                         xs: 2,
//                                         sm: 2.5,
//                                         md: 3,
//                                     },
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1.4}
//                                     alignItems="center"
//                                 >
//                                     <Box
//                                         sx={{
//                                             width: 42,
//                                             height: 42,
//                                             borderRadius:
//                                                 1.5,
//                                             display:
//                                                 "flex",
//                                             alignItems:
//                                                 "center",
//                                             justifyContent:
//                                                 "center",
//                                             background:
//                                                 "#EAF6F3",
//                                             color:
//                                                 "#0B6B5E",
//                                         }}
//                                     >
//                                         <AssignmentOutlined />
//                                     </Box>

//                                     <Box>
//                                         <Typography
//                                             sx={{
//                                                 color:
//                                                     "#102A27",
//                                                 fontSize:
//                                                     18,
//                                                 fontWeight:
//                                                     800,
//                                             }}
//                                         >
//                                             New assignment post
//                                         </Typography>

//                                         <Typography
//                                             sx={{
//                                                 mt: 0.25,
//                                                 color:
//                                                     "#71837F",
//                                                 fontSize:
//                                                     11.5,
//                                             }}
//                                         >
//                                             Complete the
//                                             details below
//                                             before sharing.
//                                         </Typography>
//                                     </Box>
//                                 </Stack>
//                             </Box>

//                             <Divider
//                                 sx={{
//                                     borderColor:
//                                         "#EDF2F1",
//                                 }}
//                             />


//                             {/* LOADING */}

//                             {fetching && (
//                                 <Box
//                                     sx={{
//                                         mx: {
//                                             xs: 2,
//                                             sm: 2.5,
//                                             md: 3,
//                                         },
//                                         mt: 2,
//                                         p: 1.5,
//                                         border:
//                                             "1px solid #DDE9E6",
//                                         borderRadius:
//                                             2,
//                                         background:
//                                             "#F5FAF9",
//                                         color:
//                                             "#0B6B5E",
//                                         fontSize:
//                                             12,
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         spacing={1}
//                                         alignItems="center"
//                                     >
//                                         <CircularProgress
//                                             size={
//                                                 17
//                                             }
//                                             thickness={
//                                                 5
//                                             }
//                                             sx={{
//                                                 color:
//                                                     "#0B6B5E",
//                                             }}
//                                         />

//                                         <span>
//                                             Loading your
//                                             teaching
//                                             subjects and
//                                             classes...
//                                         </span>
//                                     </Stack>
//                                 </Box>
//                             )}


//                             {/* ERROR */}

//                             {error && (
//                                 <Box
//                                     sx={{
//                                         mx: {
//                                             xs: 2,
//                                             sm: 2.5,
//                                             md: 3,
//                                         },
//                                         mt: 2,
//                                         p: 1.5,
//                                         border:
//                                             "1px solid #f0c9c9",
//                                         borderRadius:
//                                             2,
//                                         background:
//                                             "#fff7f7",
//                                         color:
//                                             "#a53a3a",
//                                         fontSize:
//                                             12,
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         spacing={1}
//                                         alignItems="flex-start"
//                                     >
//                                         <InfoOutlined
//                                             sx={{
//                                                 fontSize:
//                                                     18,
//                                                 mt:
//                                                     0.1,
//                                             }}
//                                         />

//                                         <span>
//                                             {error}
//                                         </span>
//                                     </Stack>
//                                 </Box>
//                             )}


//                             {/* FORM */}

//                             <Box
//                                 component="form"
//                                 onSubmit={
//                                     handleSubmit
//                                 }
//                                 sx={{
//                                     p: {
//                                         xs: 2,
//                                         sm: 2.5,
//                                         md: 3,
//                                     },
//                                 }}
//                             >

//                                 <Stack
//                                     spacing={
//                                         2.2
//                                     }
//                                 >

//                                     {/* TITLE */}

//                                     <TextField
//                                         label="Assignment title"
//                                         name="title"
//                                         value={
//                                             form.title
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         required
//                                         fullWidth
//                                         error={
//                                             Boolean(
//                                                 fieldErrors.title
//                                             )
//                                         }
//                                         helperText={
//                                             fieldErrors.title ||
//                                             `${form.title.length}/200 characters`
//                                         }
//                                         inputProps={{
//                                             maxLength: 200,
//                                         }}
//                                         placeholder="e.g. Mathematics — Fractions Practice"
//                                         sx={{
//                                             "& .MuiOutlinedInput-root":
//                                                 {
//                                                     borderRadius:
//                                                         2,
//                                                     background:
//                                                         "#FBFDFC",
//                                                     "&:hover fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#8BCDC3",
//                                                         },
//                                                     "&.Mui-focused fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#0F8B7D",
//                                                             borderWidth:
//                                                                 1.5,
//                                                         },
//                                                 },
//                                             "& .MuiInputLabel-root.Mui-focused":
//                                                 {
//                                                     color:
//                                                         "#0B6B5E",
//                                                 },
//                                         }}
//                                     />


//                                     {/* DESCRIPTION */}

//                                     <TextField
//                                         label="Assignment description"
//                                         name="description"
//                                         value={
//                                             form.description
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         multiline
//                                         minRows={
//                                             5
//                                         }
//                                         maxRows={
//                                             10
//                                         }
//                                         fullWidth
//                                         placeholder="Explain what students need to do, what they should submit, and any important instructions..."
//                                         helperText={`${form.description.length}/3000 characters`}
//                                         inputProps={{
//                                             maxLength: 3000,
//                                         }}
//                                         sx={{
//                                             "& .MuiOutlinedInput-root":
//                                                 {
//                                                     borderRadius:
//                                                         2,
//                                                     background:
//                                                         "#FBFDFC",
//                                                     alignItems:
//                                                         "flex-start",
//                                                     "&:hover fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#8BCDC3",
//                                                         },
//                                                     "&.Mui-focused fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#0F8B7D",
//                                                             borderWidth:
//                                                                 1.5,
//                                                         },
//                                                 },
//                                             "& .MuiInputLabel-root.Mui-focused":
//                                                 {
//                                                     color:
//                                                         "#0B6B5E",
//                                                 },
//                                         }}
//                                     />


//                                     {/* SUBJECT / CLASS */}

//                                     <Grid
//                                         container
//                                         spacing={2}
//                                     >

//                                         <Grid
//                                             item
//                                             xs={12}
//                                             sm={6}
//                                         >
//                                             <FormControl
//                                                 fullWidth
//                                                 required
//                                                 error={
//                                                     Boolean(
//                                                         fieldErrors.subject
//                                                     )
//                                                 }
//                                             >
//                                                 <InputLabel>
//                                                     Subject
//                                                 </InputLabel>

//                                                 <Select
//                                                     name="subject"
//                                                     value={
//                                                         form.subject
//                                                     }
//                                                     label="Subject"
//                                                     onChange={
//                                                         handleChange
//                                                     }
//                                                     startAdornment={
//                                                         <InputAdornment position="start">
//                                                             <SubjectOutlined
//                                                                 sx={{
//                                                                     ml:
//                                                                         0.5,
//                                                                     color:
//                                                                         "#0B6B5E",
//                                                                     fontSize:
//                                                                         20,
//                                                                 }}
//                                                             />
//                                                         </InputAdornment>
//                                                     }
//                                                     sx={{
//                                                         borderRadius:
//                                                             2,
//                                                         background:
//                                                             "#FBFDFC",
//                                                     }}
//                                                 >
//                                                     {subjects.length ===
//                                                     0 ? (
//                                                         <MenuItem
//                                                             disabled
//                                                         >
//                                                             No subjects
//                                                             available
//                                                         </MenuItem>
//                                                     ) : (
//                                                         subjects.map(
//                                                             (
//                                                                 subject
//                                                             ) => (
//                                                                 <MenuItem
//                                                                     key={
//                                                                         subject.id
//                                                                     }
//                                                                     value={
//                                                                         subject.id
//                                                                     }
//                                                                 >
//                                                                     {
//                                                                         subject.name
//                                                                     }
//                                                                 </MenuItem>
//                                                             )
//                                                         )
//                                                     )}
//                                                 </Select>

//                                                 <FormHelperText>
//                                                     {fieldErrors.subject ||
//                                                         "Choose the subject for this assignment."}
//                                                 </FormHelperText>
//                                             </FormControl>
//                                         </Grid>


//                                         <Grid
//                                             item
//                                             xs={12}
//                                             sm={6}
//                                         >
//                                             <FormControl
//                                                 fullWidth
//                                                 required
//                                                 error={
//                                                     Boolean(
//                                                         fieldErrors.grade_class
//                                                     )
//                                                 }
//                                             >
//                                                 <InputLabel>
//                                                     Grade / Class
//                                                 </InputLabel>

//                                                 <Select
//                                                     name="grade_class"
//                                                     value={
//                                                         form.grade_class
//                                                     }
//                                                     label="Grade / Class"
//                                                     onChange={
//                                                         handleChange
//                                                     }
//                                                     startAdornment={
//                                                         <InputAdornment position="start">
//                                                             <SchoolOutlined
//                                                                 sx={{
//                                                                     ml:
//                                                                         0.5,
//                                                                     color:
//                                                                         "#0B6B5E",
//                                                                     fontSize:
//                                                                         20,
//                                                                 }}
//                                                             />
//                                                         </InputAdornment>
//                                                     }
//                                                     sx={{
//                                                         borderRadius:
//                                                             2,
//                                                         background:
//                                                             "#FBFDFC",
//                                                     }}
//                                                 >
//                                                     {gradeClasses.length ===
//                                                     0 ? (
//                                                         <MenuItem
//                                                             disabled
//                                                         >
//                                                             No classes
//                                                             available
//                                                         </MenuItem>
//                                                     ) : (
//                                                         gradeClasses.map(
//                                                             (
//                                                                 item
//                                                             ) => (
//                                                                 <MenuItem
//                                                                     key={
//                                                                         item.id
//                                                                     }
//                                                                     value={
//                                                                         item.id
//                                                                     }
//                                                                 >
//                                                                     {getEntityLabel(
//                                                                         item
//                                                                     )}
//                                                                 </MenuItem>
//                                                             )
//                                                         )
//                                                     )}
//                                                 </Select>

//                                                 <FormHelperText>
//                                                     {fieldErrors.grade_class ||
//                                                         "Choose who should receive this assignment."}
//                                                 </FormHelperText>
//                                             </FormControl>
//                                         </Grid>
//                                     </Grid>


//                                     {/* DEADLINE */}

//                                     <TextField
//                                         type="date"
//                                         label="Due date"
//                                         name="due_date"
//                                         value={
//                                             form.due_date
//                                         }
//                                         onChange={
//                                             handleChange
//                                         }
//                                         required
//                                         fullWidth
//                                         error={
//                                             Boolean(
//                                                 fieldErrors.due_date
//                                             )
//                                         }
//                                         helperText={
//                                             fieldErrors.due_date ||
//                                             "Students will see this deadline on the assignment."
//                                         }
//                                         InputLabelProps={{
//                                             shrink: true,
//                                         }}
//                                         InputProps={{
//                                             startAdornment:
//                                                 (
//                                                     <InputAdornment position="start">
//                                                         <CalendarMonthOutlined
//                                                             sx={{
//                                                                 color:
//                                                                     "#0B6B5E",
//                                                             }}
//                                                         />
//                                                     </InputAdornment>
//                                                 ),
//                                         }}
//                                         sx={{
//                                             "& .MuiOutlinedInput-root":
//                                                 {
//                                                     borderRadius:
//                                                         2,
//                                                     background:
//                                                         "#FBFDFC",
//                                                     "&:hover fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#8BCDC3",
//                                                         },
//                                                     "&.Mui-focused fieldset":
//                                                         {
//                                                             borderColor:
//                                                                 "#0F8B7D",
//                                                         },
//                                                 },
//                                             "& .MuiInputLabel-root.Mui-focused":
//                                                 {
//                                                     color:
//                                                         "#0B6B5E",
//                                                 },
//                                         }}
//                                     />


//                                     {/* ATTACHMENT */}

//                                     <Box
//                                         sx={{
//                                             border:
//                                                 "1.5px dashed #BFD8D3",
//                                             borderRadius:
//                                                 2.5,
//                                             background:
//                                                 "#F8FCFB",
//                                             p: {
//                                                 xs: 1.8,
//                                                 sm: 2.2,
//                                             },
//                                             transition:
//                                                 "all .2s ease",
//                                             "&:hover":
//                                                 {
//                                                     borderColor:
//                                                         "#0F8B7D",
//                                                     background:
//                                                         "#F3FAF8",
//                                                 },
//                                         }}
//                                     >
//                                         {!form.file ? (
//                                             <Button
//                                                 component="label"
//                                                 fullWidth
//                                                 sx={{
//                                                     minHeight:
//                                                         100,
//                                                     display:
//                                                         "flex",
//                                                     flexDirection:
//                                                         "column",
//                                                     gap: 0.8,
//                                                     color:
//                                                         "#0B6B5E",
//                                                     textTransform:
//                                                         "none",
//                                                     borderRadius:
//                                                         2,
//                                                     "&:hover":
//                                                         {
//                                                             background:
//                                                                 "rgba(20,184,166,.05)",
//                                                         },
//                                                 }}
//                                             >
//                                                 <CloudUploadOutlined
//                                                     sx={{
//                                                         fontSize:
//                                                             34,
//                                                         color:
//                                                             "#0F8B7D",
//                                                     }}
//                                                 />

//                                                 <Typography
//                                                     sx={{
//                                                         fontSize:
//                                                             13,
//                                                         fontWeight:
//                                                             800,
//                                                     }}
//                                                 >
//                                                     Add an attachment
//                                                 </Typography>

//                                                 <Typography
//                                                     sx={{
//                                                         color:
//                                                             "#7C8F8A",
//                                                         fontSize:
//                                                             11,
//                                                     }}
//                                                 >
//                                                     Upload a
//                                                     document,
//                                                     worksheet,
//                                                     image, or
//                                                     other
//                                                     supporting
//                                                     file.
//                                                 </Typography>

//                                                 <input
//                                                     hidden
//                                                     type="file"
//                                                     ref={
//                                                         fileInputRef
//                                                     }
//                                                     onChange={
//                                                         handleFileChange
//                                                     }
//                                                 />
//                                             </Button>
//                                         ) : (
//                                             <Stack
//                                                 direction="row"
//                                                 spacing={
//                                                     1.3
//                                                 }
//                                                 alignItems="center"
//                                             >
//                                                 <Box
//                                                     sx={{
//                                                         width: 44,
//                                                         height: 44,
//                                                         borderRadius:
//                                                             1.5,
//                                                         display:
//                                                             "flex",
//                                                         alignItems:
//                                                             "center",
//                                                         justifyContent:
//                                                             "center",
//                                                         flex:
//                                                             "0 0 auto",
//                                                         background:
//                                                             "#EAF6F3",
//                                                         color:
//                                                             "#0B6B5E",
//                                                     }}
//                                                 >
//                                                     <FilePresentOutlined />
//                                                 </Box>

//                                                 <Box
//                                                     sx={{
//                                                         minWidth:
//                                                             0,
//                                                         flex: 1,
//                                                     }}
//                                                 >
//                                                     <Typography
//                                                         sx={{
//                                                             color:
//                                                                 "#173C36",
//                                                             fontSize:
//                                                                 12,
//                                                             fontWeight:
//                                                                 800,
//                                                             overflow:
//                                                                 "hidden",
//                                                             textOverflow:
//                                                                 "ellipsis",
//                                                             whiteSpace:
//                                                                 "nowrap",
//                                                         }}
//                                                     >
//                                                         {
//                                                             form.file
//                                                                 .name
//                                                         }
//                                                     </Typography>

//                                                     <Typography
//                                                         sx={{
//                                                             mt:
//                                                                 0.2,
//                                                             color:
//                                                                 "#80918D",
//                                                             fontSize:
//                                                                 10,
//                                                         }}
//                                                     >
//                                                         {
//                                                             getFileExtension(
//                                                                 form.file
//                                                                     .name
//                                                             )
//                                                         }{" "}
//                                                         •{" "}
//                                                         {
//                                                             formatFileSize(
//                                                                 form.file
//                                                                     .size
//                                                             )
//                                                         }
//                                                     </Typography>
//                                                 </Box>

//                                                 <Button
//                                                     type="button"
//                                                     onClick={
//                                                         removeFile
//                                                     }
//                                                     disabled={
//                                                         loading
//                                                     }
//                                                     sx={{
//                                                         minWidth:
//                                                             34,
//                                                         width: 34,
//                                                         height: 34,
//                                                         borderRadius:
//                                                             1.2,
//                                                         color:
//                                                             "#b14a4a",
//                                                     }}
//                                                 >
//                                                     <DeleteOutlineOutlined
//                                                         fontSize="small"
//                                                     />
//                                                 </Button>
//                                             </Stack>
//                                         )}
//                                     </Box>


//                                     {/* POST BUTTON */}

//                                     <Button
//                                         type="submit"
//                                         fullWidth
//                                         disabled={
//                                             loading ||
//                                             fetching
//                                         }
//                                         startIcon={
//                                             loading ? (
//                                                 <CircularProgress
//                                                     size={
//                                                         19
//                                                     }
//                                                     sx={{
//                                                         color:
//                                                             "#fff",
//                                                     }}
//                                                 />
//                                             ) : (
//                                                 <SendOutlined />
//                                             )
//                                         }
//                                         sx={{
//                                             minHeight:
//                                                 50,
//                                             borderRadius:
//                                                 2,
//                                             color:
//                                                 "#fff",
//                                             background:
//                                                 "linear-gradient(135deg,#075247 0%,#0B6B5E 48%,#14B8A6 100%)",
//                                             fontSize:
//                                                 13,
//                                             fontWeight:
//                                                 800,
//                                             textTransform:
//                                                 "none",
//                                             boxShadow:
//                                                 "0 9px 22px rgba(7,82,71,.18)",
//                                             "&:hover":
//                                                 {
//                                                     background:
//                                                         "linear-gradient(135deg,#06463D 0%,#095D52 48%,#0FAF9E 100%)",
//                                                     boxShadow:
//                                                         "0 12px 28px rgba(7,82,71,.24)",
//                                                 },
//                                             "&.Mui-disabled":
//                                                 {
//                                                     color:
//                                                         "rgba(255,255,255,.7)",
//                                                     background:
//                                                         "#9BB8B2",
//                                                 },
//                                         }}
//                                     >
//                                         {loading
//                                             ? "Posting assignment..."
//                                             : "Post assignment"}
//                                     </Button>

//                                 </Stack>
//                             </Box>
//                         </Paper>
//                     </Grid>


//                     {/* ====================================================
//                         LIVE PREVIEW / SIDEBAR
//                        ==================================================== */}

//                     <Grid
//                         item
//                         xs={12}
//                         md={4}
//                     >
//                         <Stack
//                             spacing={2}
//                             sx={{
//                                 position: {
//                                     xs:
//                                         "static",
//                                     md:
//                                         "sticky",
//                                 },
//                                 top: {
//                                     md: 20,
//                                 },
//                             }}
//                         >

//                             {/* PREVIEW */}

//                             <Paper
//                                 elevation={0}
//                                 sx={{
//                                     border:
//                                         "1px solid #DDE9E6",
//                                     borderRadius:
//                                         3,
//                                     background:
//                                         "#fff",
//                                     boxShadow:
//                                         "0 9px 30px rgba(7,82,71,.06)",
//                                     overflow:
//                                         "hidden",
//                                 }}
//                             >
//                                 <Box
//                                     sx={{
//                                         px: 2.2,
//                                         py: 1.8,
//                                         borderBottom:
//                                             "1px solid #EDF2F1",
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         spacing={1}
//                                         alignItems="center"
//                                     >
//                                         <Box
//                                             sx={{
//                                                 width: 34,
//                                                 height: 34,
//                                                 borderRadius:
//                                                     1.2,
//                                                 display:
//                                                     "flex",
//                                                 alignItems:
//                                                     "center",
//                                                 justifyContent:
//                                                     "center",
//                                                 background:
//                                                     "#EAF6F3",
//                                                 color:
//                                                     "#0B6B5E",
//                                             }}
//                                         >
//                                             <AutoAwesomeOutlined
//                                                 fontSize="small"
//                                             />
//                                         </Box>

//                                         <Box>
//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         "#102A27",
//                                                     fontSize:
//                                                         13,
//                                                     fontWeight:
//                                                         800,
//                                                 }}
//                                             >
//                                                 Post preview
//                                             </Typography>

//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         "#83928F",
//                                                     fontSize:
//                                                         10,
//                                                 }}
//                                             >
//                                                 How students
//                                                 will see it
//                                             </Typography>
//                                         </Box>
//                                     </Stack>
//                                 </Box>


//                                 <Box
//                                     sx={{
//                                         p: 2.2,
//                                     }}
//                                 >
//                                     <Stack
//                                         direction="row"
//                                         spacing={1}
//                                         alignItems="center"
//                                     >
//                                         <Avatar
//                                             src={
//                                                 teacherAvatar
//                                             }
//                                             sx={{
//                                                 width: 38,
//                                                 height: 38,
//                                             }}
//                                         />

//                                         <Box
//                                             sx={{
//                                                 minWidth:
//                                                     0,
//                                             }}
//                                         >
//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         "#173C36",
//                                                     fontSize:
//                                                         12,
//                                                     fontWeight:
//                                                         800,
//                                                 }}
//                                             >
//                                                 {
//                                                     teacherName
//                                                 }
//                                             </Typography>

//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         "#8A9995",
//                                                     fontSize:
//                                                         10,
//                                                 }}
//                                             >
//                                                 Teacher •
//                                                 Assignment
//                                             </Typography>
//                                         </Box>
//                                     </Stack>


//                                     <Typography
//                                         sx={{
//                                             mt: 2,
//                                             color:
//                                                 form.title
//                                                     ? "#102A27"
//                                                     : "#9AA8A5",
//                                             fontSize:
//                                                 16,
//                                             fontWeight:
//                                                 800,
//                                             lineHeight:
//                                                 1.35,
//                                             wordBreak:
//                                                 "break-word",
//                                         }}
//                                     >
//                                         {form.title ||
//                                             "Your assignment title will appear here"}
//                                     </Typography>


//                                     <Typography
//                                         sx={{
//                                             mt: 0.8,
//                                             color:
//                                                 form.description
//                                                     ? "#5D706B"
//                                                     : "#9AA8A5",
//                                             fontSize:
//                                                 11.5,
//                                             lineHeight:
//                                                 1.65,
//                                             display:
//                                                 "-webkit-box",
//                                             WebkitLineClamp:
//                                                 5,
//                                             WebkitBoxOrient:
//                                                 "vertical",
//                                             overflow:
//                                                 "hidden",
//                                             wordBreak:
//                                                 "break-word",
//                                         }}
//                                     >
//                                         {form.description ||
//                                             "Your assignment description will appear here so you can review the post before sharing it."}
//                                     </Typography>


//                                     <Stack
//                                         direction="row"
//                                         spacing={
//                                             0.7
//                                         }
//                                         flexWrap="wrap"
//                                         sx={{
//                                             mt: 1.4,
//                                             gap: 0.7,
//                                         }}
//                                     >
//                                         {selectedSubject && (
//                                             <Box
//                                                 sx={{
//                                                     px: 1,
//                                                     py: 0.45,
//                                                     borderRadius:
//                                                         999,
//                                                     background:
//                                                         "#F0F8F6",
//                                                     color:
//                                                         "#0B6B5E",
//                                                     fontSize:
//                                                         9.5,
//                                                     fontWeight:
//                                                         750,
//                                                 }}
//                                             >
//                                                 {
//                                                     getEntityLabel(
//                                                         selectedSubject
//                                                     )
//                                                 }
//                                             </Box>
//                                         )}

//                                         {selectedGradeClass && (
//                                             <Box
//                                                 sx={{
//                                                     px: 1,
//                                                     py: 0.45,
//                                                     borderRadius:
//                                                         999,
//                                                     background:
//                                                         "#F0F8F6",
//                                                     color:
//                                                         "#0B6B5E",
//                                                     fontSize:
//                                                         9.5,
//                                                     fontWeight:
//                                                         750,
//                                                 }}
//                                             >
//                                                 {
//                                                     getEntityLabel(
//                                                         selectedGradeClass
//                                                     )
//                                                 }
//                                             </Box>
//                                         )}
//                                     </Stack>


//                                     {form.due_date && (
//                                         <Box
//                                             sx={{
//                                                 display:
//                                                     "flex",
//                                                 alignItems:
//                                                     "center",
//                                                 gap: 1,
//                                                 mt: 1.5,
//                                                 p: 1,
//                                                 borderRadius:
//                                                     1.5,
//                                                 background:
//                                                     "#F5FAF9",
//                                                 border:
//                                                     "1px solid #E1EEEB",
//                                             }}
//                                         >
//                                             <CalendarMonthOutlined
//                                                 sx={{
//                                                     fontSize:
//                                                         17,
//                                                     color:
//                                                         "#0F8B7D",
//                                                 }}
//                                             />

//                                             <Box>
//                                                 <Typography
//                                                     sx={{
//                                                         color:
//                                                             "#879793",
//                                                         fontSize:
//                                                             8.5,
//                                                         fontWeight:
//                                                             800,
//                                                         textTransform:
//                                                             "uppercase",
//                                                         letterSpacing:
//                                                             ".4px",
//                                                     }}
//                                                 >
//                                                     Due date
//                                                 </Typography>

//                                                 <Typography
//                                                     sx={{
//                                                         color:
//                                                             "#35534D",
//                                                         fontSize:
//                                                             10.5,
//                                                         fontWeight:
//                                                             700,
//                                                     }}
//                                                 >
//                                                     {
//                                                         form.due_date
//                                                     }
//                                                 </Typography>
//                                             </Box>
//                                         </Box>
//                                     )}


//                                     {form.file && (
//                                         <Box
//                                             sx={{
//                                                 display:
//                                                     "flex",
//                                                 alignItems:
//                                                     "center",
//                                                 gap: 1,
//                                                 mt: 1,
//                                                 p: 1,
//                                                 borderRadius:
//                                                     1.5,
//                                                 background:
//                                                     "#F7FAF9",
//                                             }}
//                                         >
//                                             <AttachFileOutlined
//                                                 sx={{
//                                                     fontSize:
//                                                         16,
//                                                     color:
//                                                         "#0B6B5E",
//                                                 }}
//                                             />

//                                             <Typography
//                                                 sx={{
//                                                     color:
//                                                         "#536A64",
//                                                     fontSize:
//                                                         10,
//                                                     overflow:
//                                                         "hidden",
//                                                     textOverflow:
//                                                         "ellipsis",
//                                                     whiteSpace:
//                                                         "nowrap",
//                                                 }}
//                                             >
//                                                 {
//                                                     form.file
//                                                         .name
//                                                 }
//                                             </Typography>
//                                         </Box>
//                                     )}
//                                 </Box>
//                             </Paper>


//                             {/* COMMUNITY NOTE */}

//                             <Paper
//                                 elevation={0}
//                                 sx={{
//                                     border:
//                                         "1px solid #DDE9E6",
//                                     borderRadius:
//                                         3,
//                                     p: 2.2,
//                                     background:
//                                         "linear-gradient(145deg,#ffffff,#F3F9F7)",
//                                     boxShadow:
//                                         "0 8px 25px rgba(7,82,71,.05)",
//                                 }}
//                             >
//                                 <Stack
//                                     direction="row"
//                                     spacing={1}
//                                     alignItems="flex-start"
//                                 >
//                                     <Box
//                                         sx={{
//                                             width: 34,
//                                             height: 34,
//                                             borderRadius:
//                                                 1.2,
//                                             display:
//                                                 "flex",
//                                             alignItems:
//                                                 "center",
//                                             justifyContent:
//                                                 "center",
//                                             flex:
//                                                 "0 0 auto",
//                                             background:
//                                                 "#EAF6F3",
//                                             color:
//                                                 "#0B6B5E",
//                                         }}
//                                     >
//                                         <InfoOutlined
//                                             fontSize="small"
//                                         />
//                                     </Box>

//                                     <Box>
//                                         <Typography
//                                             sx={{
//                                                 color:
//                                                     "#173C36",
//                                                 fontSize:
//                                                     12,
//                                                 fontWeight:
//                                                     800,
//                                             }}
//                                         >
//                                             Make your post useful
//                                         </Typography>

//                                         <Typography
//                                             sx={{
//                                                 mt: 0.6,
//                                                 color:
//                                                     "#71827D",
//                                                 fontSize:
//                                                     10.5,
//                                                 lineHeight:
//                                                     1.65,
//                                             }}
//                                         >
//                                             Give students
//                                             enough context
//                                             to understand
//                                             the task without
//                                             needing to guess
//                                             what you expect.
//                                         </Typography>
//                                     </Box>
//                                 </Stack>
//                             </Paper>


//                             {/* POST CHECKLIST */}

//                             <Paper
//                                 elevation={0}
//                                 sx={{
//                                     border:
//                                         "1px solid #DDE9E6",
//                                     borderRadius:
//                                         3,
//                                     p: 2.2,
//                                     background:
//                                         "#fff",
//                                     boxShadow:
//                                         "0 8px 25px rgba(7,82,71,.05)",
//                                 }}
//                             >
//                                 <Typography
//                                     sx={{
//                                         color:
//                                             "#173C36",
//                                         fontSize:
//                                             12,
//                                         fontWeight:
//                                             800,
//                                         mb: 1.3,
//                                     }}
//                                 >
//                                     Before you post
//                                 </Typography>

//                                 <Stack
//                                     spacing={
//                                         1
//                                     }
//                                 >
//                                     {[
//                                         {
//                                             label:
//                                                 "Assignment title added",
//                                             done:
//                                                 Boolean(
//                                                     form.title.trim()
//                                                 ),
//                                         },
//                                         {
//                                             label:
//                                                 "Subject selected",
//                                             done:
//                                                 Boolean(
//                                                     form.subject
//                                                 ),
//                                         },
//                                         {
//                                             label:
//                                                 "Class selected",
//                                             done:
//                                                 Boolean(
//                                                     form.grade_class
//                                                 ),
//                                         },
//                                         {
//                                             label:
//                                                 "Due date selected",
//                                             done:
//                                                 Boolean(
//                                                     form.due_date
//                                                 ),
//                                         },
//                                     ].map(
//                                         (
//                                             item
//                                         ) => (
//                                             <Stack
//                                                 key={
//                                                     item.label
//                                                 }
//                                                 direction="row"
//                                                 spacing={
//                                                     0.8
//                                                 }
//                                                 alignItems="center"
//                                             >
//                                                 <CheckCircleOutlined
//                                                     sx={{
//                                                         fontSize:
//                                                             16,
//                                                         color:
//                                                             item.done
//                                                                 ? "#0F8B7D"
//                                                                 : "#C4D3CF",
//                                                     }}
//                                                 />

//                                                 <Typography
//                                                     sx={{
//                                                         color:
//                                                             item.done
//                                                                 ? "#42605A"
//                                                                 : "#879793",
//                                                         fontSize:
//                                                             10.5,
//                                                     }}
//                                                 >
//                                                     {
//                                                         item.label
//                                                     }
//                                                 </Typography>
//                                             </Stack>
//                                         )
//                                     )}
//                                 </Stack>
//                             </Paper>

//                         </Stack>
//                     </Grid>
//                 </Grid>
//             </Container>
//         </Box>
//     );
// };

// export default PostAssignment;